import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { notificacionesService } from '../../services/notificaciones';

const POLL_MS = 30000;

const NotificationBell = () => {
    const [abierto, setAbierto] = useState(false);
    const [noLeidas, setNoLeidas] = useState(0);
    const [notificaciones, setNotificaciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const contenedorRef = useRef(null);
    const navigate = useNavigate();

    const cargarContador = useCallback(async () => {
        try {
            const total = await notificacionesService.contador();
            setNoLeidas(total);
        } catch {
            /* silencioso: no bloquea la UI si falla el contador */
        }
    }, []);

    useEffect(() => {
        cargarContador();
        const interval = setInterval(cargarContador, POLL_MS);
        return () => clearInterval(interval);
    }, [cargarContador]);

    useEffect(() => {
        const handleClickFuera = (e) => {
            if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
                setAbierto(false);
            }
        };
        document.addEventListener('mousedown', handleClickFuera);
        return () => document.removeEventListener('mousedown', handleClickFuera);
    }, []);

    const abrirPanel = async () => {
        const nuevoEstado = !abierto;
        setAbierto(nuevoEstado);
        if (nuevoEstado) {
            setCargando(true);
            try {
                const data = await notificacionesService.listar(false, 15);
                setNotificaciones(data);
            } catch {
                /* silencioso */
            } finally {
                setCargando(false);
            }
        }
    };

    const handleClickNotificacion = async (n) => {
        if (!n.leido) {
            try {
                await notificacionesService.marcarLeida(n.id);
                setNotificaciones((prev) => prev.map((x) => (x.id === n.id ? { ...x, leido: true } : x)));
                setNoLeidas((prev) => Math.max(0, prev - 1));
            } catch {
                /* silencioso */
            }
        }
        if (n.solicitud_id) {
            setAbierto(false);
            navigate('/job-application');
        }
    };

    const handleMarcarTodas = async () => {
        try {
            await notificacionesService.marcarTodasLeidas();
            setNotificaciones((prev) => prev.map((n) => ({ ...n, leido: true })));
            setNoLeidas(0);
        } catch {
            /* silencioso */
        }
    };

    return (
        <div className="relative" ref={contenedorRef}>
            <button
                onClick={abrirPanel}
                className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                title="Notificaciones"
            >
                <Bell className="h-5 w-5" />
                {noLeidas > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                        {noLeidas > 9 ? '9+' : noLeidas}
                    </span>
                )}
            </button>

            {abierto && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <span className="font-semibold text-sm text-gray-800">Notificaciones</span>
                        {noLeidas > 0 && (
                            <button
                                onClick={handleMarcarTodas}
                                className="text-xs text-blue-600 hover:underline font-medium"
                            >
                                Marcar todas como leídas
                            </button>
                        )}
                    </div>

                    {cargando && (
                        <div className="px-4 py-8 text-center text-sm text-gray-400">Cargando...</div>
                    )}

                    {!cargando && notificaciones.length === 0 && (
                        <div className="px-4 py-8 text-center text-sm text-gray-400">No tienes notificaciones.</div>
                    )}

                    {!cargando && notificaciones.map((n) => (
                        <button
                            key={n.id}
                            onClick={() => handleClickNotificacion(n)}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                                !n.leido ? 'bg-blue-50/50' : ''
                            }`}
                        >
                            <div className="flex items-start gap-2">
                                {!n.leido && <span className="h-2 w-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{n.titulo}</p>
                                    {n.mensaje && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.mensaje}</p>}
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        {new Date(n.created_at).toLocaleString('es-PE')}
                                    </p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
